import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave, faSpinner, faTrash, faPlus, faExclamationTriangle, faArchive, faBoxOpen,
} from '@fortawesome/free-solid-svg-icons';
import AppModal from '../../../components/common/AppModal';
import VarPhotoGrid from '../../../components/explorers/varExplorer/VarPhotoGrid';
import { usePhotoLoader, type VARPhoto } from '../../../components/explorers/varExplorer/usePhotoLoader';
import MultiFileUploader from '../../../components/upload/MultiFileUploader';
import { usePermissions } from '../../../context/PermissionContext';
import {
  updateVar, deleteVar, archiveVar, unarchiveVar, setCmsPhotoState, deletePhotoEverywhere,
  validateVarLocation,
} from '../../../services/varPhotoApi';
import {
  VAR_SUBCATEGORY_MAP, type UpdateVARInput, type VARSubcategory, type Occupancy,
  type RedFlagStatus,
} from '../../../types/var';
import { CoordinateInput } from '../../../components/common/CoordinateInput';
import {
  geoPointToLatLon, locationChanged, toLocationPayload, evaluateRelocation,
  type LatLon, type RelocationStatus,
} from './varLocation';
import './VAREditModal.scss';

const SUBCATEGORIES = Object.keys(VAR_SUBCATEGORY_MAP) as VARSubcategory[];

const OCCUPANCY_OPTIONS: Occupancy[] = ['None', 'Periodic', 'Year Round', 'Unknown'];
const RED_FLAG_OPTIONS: RedFlagStatus[] = ['Active', 'Resolved', 'Cancelled'];

type TransitionMode = 'none' | 'archive' | 'unarchive' | 'delete';

interface OpenDetail {
  varId: string;
}

const VAREditModal: React.FC = () => {
  const [varId, setVarId] = useState<string | null>(null);
  const { entity, photos, loading, error, refresh } = usePhotoLoader(varId, !!varId);

  // Form state — initialised from entity once it loads
  const [name, setName] = useState('');
  const [details, setDetails] = useState('');
  const [subtype, setSubtype] = useState<VARSubcategory | ''>('');
  const [occupancy, setOccupancy] = useState<Occupancy | ''>('');
  const [keywords, setKeywords] = useState('');

  // Location (#679) — relocation is a within-region/park nudge; the backend
  // rejects out-of-bounds or cross-region moves. `location` is null when the
  // user has entered coordinates that cannot be parsed.
  const [location, setLocation] = useState<LatLon | null>(null);
  const [initialLocation, setInitialLocation] = useState<LatLon | null>(null);
  const [relocationStatus, setRelocationStatus] = useState<RelocationStatus>('ok');

  // Access flags
  const [accessRoad, setAccessRoad] = useState(false);
  const [accessFW, setAccessFW] = useState(false);
  const [accessRW, setAccessRW] = useState(false);
  const [accessBoat, setAccessBoat] = useState(false);

  // Verification
  const [verDate, setVerDate] = useState('');
  const [verMethod, setVerMethod] = useState('');
  const [verNotes, setVerNotes] = useState('');
  const [verBy, setVerBy] = useState('');

  // Contact
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // Red flag
  const [redFlagActive, setRedFlagActive] = useState(false);
  const [redFlagStatus, setRedFlagStatus] = useState<RedFlagStatus | ''>('');
  const [redFlagReason, setRedFlagReason] = useState('');

  // Sprinkler
  const [sprinklerActive, setSprinklerActive] = useState(false);
  const [sprinklerReason, setSprinklerReason] = useState('');
  const [sprinklerName, setSprinklerName] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showAddPhotos, setShowAddPhotos] = useState(false);
  const [transition, setTransition] = useState<TransitionMode>('none');
  const [transitionReason, setTransitionReason] = useState('');
  const [transitioning, setTransitioning] = useState(false);

  const { canDo } = usePermissions();
  const canArchive = canDo('var', 'archive');
  const canDelete = canDo('var', 'delete');

  // Action bar lives at the bottom of the modal; when the transition prompt
  // opens, its input + buttons sit below the fold of the scroll viewport.
  // Scroll the action bar fully into view so the Archive / Cancel controls
  // are visible without the user having to scroll.
  const actionsRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (transition !== 'none' && actionsRef.current) {
      actionsRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, [transition]);

  // Listen for varEdit:open events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenDetail>).detail;
      setVarId(detail.varId);
      setShowAddPhotos(false);
      setTransition('none');
      setTransitionReason('');
      setSaveError(null);
    };
    window.addEventListener('varEdit:open', handler);
    return () => window.removeEventListener('varEdit:open', handler);
  }, []);

  // Hydrate form when entity loads
  useEffect(() => {
    if (!entity) return;
    setName(entity.name || '');
    setDetails(entity.details || '');
    setSubtype((entity.var_subtype as VARSubcategory) || '');
    setOccupancy(entity.occupancy || '');
    setKeywords((entity.keywords || []).join(', '));

    const loc = geoPointToLatLon(entity.location);
    setLocation(loc);
    setInitialLocation(loc);
    setRelocationStatus('ok');

    setAccessRoad(entity.access?.Road ?? false);
    setAccessFW(entity.access?.AircraftFW ?? false);
    setAccessRW(entity.access?.AircraftRW ?? false);
    setAccessBoat(entity.access?.BoatRiver ?? false);

    setVerDate(entity.verification?.Date || '');
    setVerMethod(entity.verification?.Method || '');
    setVerNotes(entity.verification?.Notes || '');
    setVerBy(entity.verification?.VerifiedBy || '');

    setContactName(entity.contact?.name || '');
    setContactPhone(entity.contact?.phone || '');
    setContactEmail(entity.contact?.email || '');

    setRedFlagActive(entity.red_flag_active ?? false);
    setRedFlagStatus(entity.red_flag_status || '');
    setRedFlagReason(entity.red_flag_reason || '');

    setSprinklerActive(entity.sprinklerFlag?.active ?? false);
    setSprinklerReason(entity.sprinklerFlag?.reason || '');
    setSprinklerName(entity.sprinklerFlag?.name || '');
  }, [entity]);

  const derivedCategory = useMemo(() => subtype ? VAR_SUBCATEGORY_MAP[subtype] : null, [subtype]);

  // Update location and live-check whether the new point leaves this VAR's
  // region/park code (the warning is shown only then — see the Location section).
  const handleLocationChange = useCallback((coords: LatLon | null) => {
    setLocation(coords);
    if (!coords) { setRelocationStatus('ok'); return; }
    if (!locationChanged(initialLocation, coords)) { setRelocationStatus('ok'); return; }
    validateVarLocation(coords.lat, coords.lon)
      .then((result) => setRelocationStatus(evaluateRelocation(entity?.region ?? null, result)))
      .catch(() => setRelocationStatus('ok'));
  }, [entity?.region, initialLocation]);

  const handleClose = useCallback(() => {
    setVarId(null);
    setSaveError(null);
    setShowAddPhotos(false);
    setTransition('none');
    setTransitionReason('');
  }, []);

  const handleSave = useCallback(async () => {
    if (!entity) return;

    // A null location means the user typed coordinates that don't parse.
    if (location === null) {
      setSaveError('Enter a valid location (latitude and longitude).');
      return;
    }
    // Block a save that the backend would reject anyway (out-of-bounds or a
    // cross-region move) so the user fixes it here first.
    if (relocationStatus !== 'ok') {
      setSaveError(relocationStatus === 'out_of_bounds'
        ? 'This location is outside the NWT fire management boundaries.'
        : 'This location is in a different region/park. Archive this VAR and create a new one for the new location.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    // status is managed exclusively via the archive/unarchive endpoints,
    // so it's deliberately NOT included in the update payload here.
    const payload: UpdateVARInput = {
      name: name.trim(),
      details: details.trim(),
      var_subtype: subtype || undefined,
      var_type: derivedCategory || undefined,
      occupancy: occupancy || undefined,
      keywords: keywords.split(',').map((k) => k.trim()).filter(Boolean),
      access: {
        Road: accessRoad,
        AircraftFW: accessFW,
        AircraftRW: accessRW,
        BoatRiver: accessBoat,
      },
      verification: {
        Date: verDate || null,
        Method: verMethod,
        Notes: verNotes,
        VerifiedBy: verBy,
      },
      contact: {
        name: contactName,
        phone: contactPhone,
        email: contactEmail,
      },
      red_flag_active: redFlagActive,
      red_flag_status: redFlagStatus || undefined,
      red_flag_reason: redFlagReason,
      sprinklerFlag: {
        active: sprinklerActive,
        reason: sprinklerReason,
        name: sprinklerName,
        sprinkerlocaldate: entity.sprinklerFlag?.sprinkerlocaldate || '',
        created_by: entity.sprinklerFlag?.created_by,
      },
      // Only send location when it actually moved, so unrelated edits don't
      // trigger a needless region re-derivation on the backend.
      ...(locationChanged(initialLocation, location) ? { location: toLocationPayload(location) } : {}),
    };

    const result = await updateVar(entity.id, payload);
    setSaving(false);
    if (!result.ok) {
      setSaveError(result.error || 'Save failed');
      return;
    }
    window.dispatchEvent(new CustomEvent('varDataChanged', { detail: { varId: entity.var_id } }));
    handleClose();
  }, [
    entity, name, details, subtype, derivedCategory, occupancy, keywords,
    location, initialLocation, relocationStatus,
    accessRoad, accessFW, accessRW, accessBoat,
    verDate, verMethod, verNotes, verBy,
    contactName, contactPhone, contactEmail,
    redFlagActive, redFlagStatus, redFlagReason,
    sprinklerActive, sprinklerReason, sprinklerName,
    handleClose,
  ]);

  const handleDeletePhoto = useCallback(async (photo: VARPhoto) => {
    if (entity && photo.pgIndex !== null) {
      await deletePhotoEverywhere(entity.id, photo.pgIndex, photo.id, photo.imageFileUuid);
    } else {
      // Legacy CSV photo not in PG; just delete CMS node + file.
      const { deleteCmsPhotoAndFile } = await import('../../../services/varPhotoApi');
      await deleteCmsPhotoAndFile(photo.id, photo.imageFileUuid);
    }
    refresh();
    window.dispatchEvent(new CustomEvent('varDataChanged', { detail: { varId: entity?.var_id } }));
  }, [entity, refresh]);

  // Push the parent VAR's lifecycle state to each photo's CMS node so the
  // CMS file view can filter by it without depending on Drupal's publish
  // flag. Best-effort per node — a CMS hiccup doesn't block the PG-side
  // transition (audit_log still captures the operator's intent).
  const propagatePhotoState = useCallback(async (state: 'Active' | 'Archived'): Promise<void> => {
    if (!photos.length) return;
    await Promise.all(photos.map((p) => setCmsPhotoState(p.id, state)));
  }, [photos]);

  const handleArchive = useCallback(async () => {
    if (!entity) return;
    setTransitioning(true);
    setSaveError(null);
    const result = await archiveVar(entity.id, transitionReason);
    if (!result.ok) {
      setTransitioning(false);
      setSaveError(result.error || 'Archive failed');
      return;
    }
    await propagatePhotoState('Archived');
    setTransitioning(false);
    window.dispatchEvent(new CustomEvent('varDataChanged', { detail: { varId: entity.var_id } }));
    handleClose();
  }, [entity, transitionReason, propagatePhotoState, handleClose]);

  const handleUnarchive = useCallback(async () => {
    if (!entity) return;
    setTransitioning(true);
    setSaveError(null);
    const result = await unarchiveVar(entity.id, transitionReason);
    if (!result.ok) {
      setTransitioning(false);
      setSaveError(result.error || 'Unarchive failed');
      return;
    }
    await propagatePhotoState('Active');
    setTransitioning(false);
    window.dispatchEvent(new CustomEvent('varDataChanged', { detail: { varId: entity.var_id } }));
    handleClose();
  }, [entity, transitionReason, propagatePhotoState, handleClose]);

  const handleHardDelete = useCallback(async () => {
    if (!entity) return;
    setTransitioning(true);
    setSaveError(null);

    // Drop the CMS photo nodes + files first (node before file; file is
    // blocked by Drupal if still referenced). This is the only path that
    // removes anything from CMS.
    const { deleteCmsPhotoAndFile } = await import('../../../services/varPhotoApi');
    await Promise.all(photos.map((p) => deleteCmsPhotoAndFile(p.id, p.imageFileUuid)));

    const result = await deleteVar(entity.id, transitionReason);
    setTransitioning(false);
    if (!result.ok) {
      setSaveError(result.error || 'Delete failed');
      return;
    }
    window.dispatchEvent(new CustomEvent('varDataChanged', { detail: { varId: entity.var_id, deleted: true } }));
    handleClose();
  }, [entity, photos, transitionReason, handleClose]);

  const openTransition = useCallback((mode: TransitionMode) => {
    setTransition(mode);
    setTransitionReason('');
    setSaveError(null);
  }, []);

  const cancelTransition = useCallback(() => {
    setTransition('none');
    setTransitionReason('');
  }, []);

  if (!varId) return null;

  return (
    <>
      <AppModal isOpen={!!varId} onClose={handleClose} width={680} maxHeight="90vh">
        <div className="var-edit">
          <h3 className="var-edit__title">Edit VAR {entity?.var_id || ''}</h3>

          {loading && <p className="var-edit__loading"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</p>}
          {error && <p className="var-edit__error">{error}</p>}

          {entity && entity.status === 'Archived' && (
            <div className="var-edit__archived-banner">
              <FontAwesomeIcon icon={faArchive} />
              <div>
                <strong>Archived</strong>
                {entity.archive_reason && <span className="var-edit__archived-reason"> — {entity.archive_reason}</span>}
              </div>
            </div>
          )}

          {entity && !loading && (
            <>
              <section className="var-edit__section">
                <h4>Basic</h4>
                <div className="var-edit__field">
                  <label>Name <span className="required">*</span></label>
                  <input value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="var-edit__field">
                  <label>Details</label>
                  <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} />
                </div>
                <div className="var-edit__row">
                  <div className="var-edit__field">
                    <label>Subtype</label>
                    <select value={subtype} onChange={(e) => setSubtype(e.target.value as VARSubcategory)}>
                      <option value="">--</option>
                      {SUBCATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="var-edit__field">
                    <label>Category</label>
                    <input value={derivedCategory || ''} disabled readOnly />
                  </div>
                </div>
                <div className="var-edit__field">
                  <label>Occupancy</label>
                  <select value={occupancy} onChange={(e) => setOccupancy(e.target.value as Occupancy)}>
                    <option value="">--</option>
                    {OCCUPANCY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="var-edit__field">
                  <label>Keywords (comma-separated)</label>
                  <input value={keywords} onChange={(e) => setKeywords(e.target.value)} />
                </div>
              </section>

              <details className="var-edit__section var-edit__section--collapsible">
                <summary>Location</summary>
                <CoordinateInput lat={location?.lat ?? null} lon={location?.lon ?? null} onChange={handleLocationChange} />
                {relocationStatus === 'region_change' && (
                  <p className="var-edit__hint var-edit__hint--error" role="alert">
                    This location is in a different region/park than {entity.var_id}. A VAR can only
                    be moved within its own region/park — archive this VAR and create a new one for
                    the new location.
                  </p>
                )}
                {relocationStatus === 'out_of_bounds' && (
                  <p className="var-edit__hint var-edit__hint--error" role="alert">
                    This location is outside the NWT fire management boundaries.
                  </p>
                )}
              </details>

              <section className="var-edit__section">
                <h4>Photos ({photos.length})</h4>
                <VarPhotoGrid
                  photos={photos}
                  editable
                  onDelete={handleDeletePhoto}
                  emptyMessage="No photos yet"
                />
                <button
                  type="button"
                  className="var-edit__add-photos"
                  onClick={() => setShowAddPhotos(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Photos
                </button>
              </section>

              <details className="var-edit__section var-edit__section--collapsible">
                <summary>Access</summary>
                <div className="var-edit__checks">
                  <label><input type="checkbox" checked={accessRoad} onChange={(e) => setAccessRoad(e.target.checked)} /> Road</label>
                  <label><input type="checkbox" checked={accessFW} onChange={(e) => setAccessFW(e.target.checked)} /> Aircraft (Fixed-wing)</label>
                  <label><input type="checkbox" checked={accessRW} onChange={(e) => setAccessRW(e.target.checked)} /> Aircraft (Rotary)</label>
                  <label><input type="checkbox" checked={accessBoat} onChange={(e) => setAccessBoat(e.target.checked)} /> Boat / River</label>
                </div>
              </details>

              <details className="var-edit__section var-edit__section--collapsible">
                <summary>Verification</summary>
                <div className="var-edit__row">
                  <div className="var-edit__field">
                    <label>Date</label>
                    <input type="date" value={verDate || ''} onChange={(e) => setVerDate(e.target.value)} />
                  </div>
                  <div className="var-edit__field">
                    <label>Method</label>
                    <input value={verMethod} onChange={(e) => setVerMethod(e.target.value)} />
                  </div>
                </div>
                <div className="var-edit__field">
                  <label>Verified By</label>
                  <input value={verBy} onChange={(e) => setVerBy(e.target.value)} />
                </div>
                <div className="var-edit__field">
                  <label>Notes</label>
                  <textarea value={verNotes} onChange={(e) => setVerNotes(e.target.value)} rows={2} />
                </div>
              </details>

              <details className="var-edit__section var-edit__section--collapsible">
                <summary>Contact</summary>
                <div className="var-edit__row">
                  <div className="var-edit__field">
                    <label>Name</label>
                    <input value={contactName} onChange={(e) => setContactName(e.target.value)} />
                  </div>
                  <div className="var-edit__field">
                    <label>Phone</label>
                    <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                  </div>
                </div>
                <div className="var-edit__field">
                  <label>Email</label>
                  <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                </div>
              </details>

              <details className="var-edit__section var-edit__section--collapsible">
                <summary>Red Flag</summary>
                <label className="var-edit__inline-check">
                  <input type="checkbox" checked={redFlagActive} onChange={(e) => setRedFlagActive(e.target.checked)} />
                  Active
                </label>
                <div className="var-edit__row">
                  <div className="var-edit__field">
                    <label>Status</label>
                    <select value={redFlagStatus} onChange={(e) => setRedFlagStatus(e.target.value as RedFlagStatus)}>
                      <option value="">--</option>
                      {RED_FLAG_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="var-edit__field">
                  <label>Reason</label>
                  <textarea value={redFlagReason} onChange={(e) => setRedFlagReason(e.target.value)} rows={2} />
                </div>
              </details>

              <details className="var-edit__section var-edit__section--collapsible">
                <summary>Sprinkler</summary>
                <label className="var-edit__inline-check">
                  <input type="checkbox" checked={sprinklerActive} onChange={(e) => setSprinklerActive(e.target.checked)} />
                  Active
                </label>
                <div className="var-edit__field">
                  <label>Name</label>
                  <input value={sprinklerName} onChange={(e) => setSprinklerName(e.target.value)} />
                </div>
                <div className="var-edit__field">
                  <label>Reason</label>
                  <textarea value={sprinklerReason} onChange={(e) => setSprinklerReason(e.target.value)} rows={2} />
                </div>
              </details>

              {saveError && (
                <div className="var-edit__error">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> {saveError}
                </div>
              )}

              <div className="var-edit__actions" ref={actionsRef}>
                <div className="var-edit__actions-left">
                  {transition === 'none' ? (
                    <>
                      {entity.status === 'Active' && canArchive && (
                        <button
                          type="button"
                          className="var-edit__btn var-edit__btn--secondary"
                          onClick={() => openTransition('archive')}
                          disabled={saving || transitioning}
                        >
                          <FontAwesomeIcon icon={faArchive} /> Archive
                        </button>
                      )}
                      {entity.status === 'Archived' && canArchive && (
                        <button
                          type="button"
                          className="var-edit__btn var-edit__btn--secondary"
                          onClick={handleUnarchive}
                          disabled={saving || transitioning}
                        >
                          {transitioning
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Unarchiving…</>
                            : <><FontAwesomeIcon icon={faBoxOpen} /> Unarchive</>}
                        </button>
                      )}
                      {entity.status === 'Archived' && canDelete && (
                        <button
                          type="button"
                          className="var-edit__btn var-edit__btn--danger"
                          onClick={() => openTransition('delete')}
                          disabled={saving || transitioning}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete VAR
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="var-edit__transition-prompt">
                      <label className="var-edit__transition-label">
                        {transition === 'archive' && 'Reason for archiving:'}
                        {transition === 'unarchive' && 'Reason for unarchiving:'}
                        {transition === 'delete' && `Reason for deleting this VAR and all ${photos.length} photo(s):`}
                      </label>
                      <input
                        type="text"
                        className="var-edit__transition-reason"
                        value={transitionReason}
                        onChange={(e) => setTransitionReason(e.target.value)}
                        placeholder="No reason given"
                        autoFocus
                      />
                      <div className="var-edit__transition-buttons">
                        <button
                          type="button"
                          className={`var-edit__btn ${transition === 'delete' ? 'var-edit__btn--danger' : 'var-edit__btn--primary'}`}
                          onClick={() => {
                            if (transition === 'archive') handleArchive();
                            else if (transition === 'unarchive') handleUnarchive();
                            else if (transition === 'delete') handleHardDelete();
                          }}
                          disabled={transitioning}
                        >
                          {transitioning
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Working…</>
                            : transition === 'archive' ? 'Archive'
                            : transition === 'unarchive' ? 'Unarchive'
                            : 'Yes, delete'}
                        </button>
                        <button
                          type="button"
                          className="var-edit__btn var-edit__btn--secondary"
                          onClick={cancelTransition}
                          disabled={transitioning}
                        >Cancel</button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="var-edit__actions-right">
                  <button
                    type="button"
                    className="var-edit__btn var-edit__btn--secondary"
                    onClick={handleClose}
                    disabled={saving || transitioning}
                  >Cancel</button>
                  <button
                    type="button"
                    className="var-edit__btn var-edit__btn--primary"
                    onClick={handleSave}
                    disabled={saving || transitioning || !name.trim() || transition !== 'none'}
                  >
                    {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faSave} /> Save</>}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </AppModal>

      {entity && (
        <MultiFileUploader
          contentType="var_photo"
          context={{ varId: entity.var_id }}
          identifier={entity.var_id}
          isOpen={showAddPhotos}
          onClose={() => setShowAddPhotos(false)}
          onUploadComplete={() => { refresh(); }}
        />
      )}
    </>
  );
};

export default VAREditModal;
